// src/features/auth/AuthRealtimeMonitor.ts
import { db } from '../../lib/firebase';
import { collection, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { AuthCacheManager } from './AuthCacheManager';
import { AuthRealtimeMonitor } from './types';

class AuthRealtimeMonitorImpl implements AuthRealtimeMonitor {
  subscriptions: Map<string, Unsubscribe> = new Map();
  activeSubscriptions: Set<string> = new Set();
  
  /**
   * 最小限の購読スコープで初期化
   */
  initializeSubscriptions(userId: string, currentWorkspaceId?: string) {
    // 1. ユーザーのワークスペース一覧（必須）
    this.subscribeToUserWorkspaces(userId);
    
    // 2. 現在開いているワークスペースのメンバー（条件付き）
    if (currentWorkspaceId) {
      this.subscribeToWorkspaceMembers(currentWorkspaceId);
    }
  }
  
  /**
   * ワークスペース切り替え時の動的購読
   */
  switchWorkspace(newWorkspaceId: string) {
    // 古いワークスペースの購読を停止
    this.unsubscribeFromWorkspaceMembers();
    
    // 新しいワークスペースの購読を開始
    this.subscribeToWorkspaceMembers(newWorkspaceId);
  }
  
  /**
   * ユーザーのワークスペース一覧を購読
   * 注意: この関数は users/{userId}/workspaces からワークスペースIDを取得し、
   * 各ワークスペースの members/{userId} から実際の権限情報を取得します
   */
  private subscribeToUserWorkspaces(userId: string) {
    const key = `user_workspaces_${userId}`;
    const subscribeStartTime = performance.now();

    const staleKeys = Array.from(this.activeSubscriptions).filter(
      (activeKey) => activeKey.startsWith('user_workspaces_') && activeKey !== key
    );
    staleKeys.forEach((staleKey) => this.unsubscribe(staleKey));

    // 既に購読中の場合はスキップ
    if (this.activeSubscriptions.has(key)) {
      return;
    }

    try {
      const unsubscribe = onSnapshot(
        collection(db, `users/${userId}/workspaces`),
        async (snapshot) => {
          const snapshotTime = performance.now();

          // ワークスペースIDのリストを取得
          const workspaceIds = snapshot.docs.map(doc => doc.id);

          // 各ワークスペースのメンバー情報から権限を取得
          const memberUpdateStartTime = performance.now();
          await AuthCacheManager.updateUserWorkspacesFromMembers(userId, workspaceIds);
          const memberUpdateEndTime = performance.now();
        },
        (error) => {
          const errorTime = performance.now();
          console.error('📡 [AuthRealtimeMonitor] ユーザーワークスペース購読エラー:', {
            error,
            timestamp: new Date().toISOString(),
            userId,
            elapsedTime: errorTime - subscribeStartTime
          });
        }
      );

      this.subscriptions.set(key, unsubscribe);
      this.activeSubscriptions.add(key);

      const subscribeEndTime = performance.now();
    } catch (error) {
      const errorTime = performance.now();
      console.error('📡 [AuthRealtimeMonitor] ユーザーワークスペース購読開始エラー:', {
        error,
        timestamp: new Date().toISOString(),
        userId,
        elapsedTime: errorTime - subscribeStartTime
      });
    }
  }
  
  /**
   * ワークスペースのメンバー一覧を購読
   */
  private subscribeToWorkspaceMembers(workspaceId: string) {
    const key = `workspace_members_${workspaceId}`;
    
    // 既に購読中の場合はスキップ
    if (this.activeSubscriptions.has(key)) {
      return;
    }
    
    try {
      const unsubscribe = onSnapshot(
        collection(db, `workspaces/${workspaceId}/members`),
        (snapshot) => {
          AuthCacheManager.updateWorkspaceMembers(workspaceId, snapshot);
        },
        (error) => {
          console.error('❌ AuthRealtimeMonitor: ワークスペースメンバー購読エラー:', error);
        }
      );

      this.subscriptions.set(key, unsubscribe);
      this.activeSubscriptions.add(key);
    } catch (error) {
      console.error('❌ AuthRealtimeMonitor: ワークスペースメンバー購読開始エラー:', error);
    }
  }
  
  /**
   * ワークスペースメンバーの購読を停止
   */
  private unsubscribeFromWorkspaceMembers() {
    const keysToRemove: string[] = [];
    
    // Setのイテレーションを修正
    this.activeSubscriptions.forEach(key => {
      if (key.startsWith('workspace_members_')) {
        const unsubscribe = this.subscriptions.get(key);
        if (unsubscribe) {
          unsubscribe();
          this.subscriptions.delete(key);
          keysToRemove.push(key);
        }
      }
    });
    
    keysToRemove.forEach(key => {
      this.activeSubscriptions.delete(key);
    });
  }
  
  /**
   * 特定の購読を停止
   */
  unsubscribe(key: string) {
    const unsubscribe = this.subscriptions.get(key);
    if (unsubscribe) {
      unsubscribe();
      this.subscriptions.delete(key);
      this.activeSubscriptions.delete(key);
    }
  }
  
  /**
   * 全ての購読をクリーンアップ
   */
  cleanup() {
    // Map.entries()のイテレーションを修正
    this.subscriptions.forEach((unsubscribe, key) => {
      unsubscribe();
    });

    this.subscriptions.clear();
    this.activeSubscriptions.clear();
  }
  
  /**
   * 現在の購読状態を取得
   */
  getSubscriptionStatus() {
    return {
      activeSubscriptions: Array.from(this.activeSubscriptions),
      subscriptionCount: this.subscriptions.size
    };
  }
}

// シングルトンインスタンス
export const authRealtimeMonitor = new AuthRealtimeMonitorImpl();

// クラスもエクスポート（必要に応じて）
export { AuthRealtimeMonitorImpl };
